import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft, RefreshCw, Sliders, Cpu, MemoryStick, HardDrive, Loader2, Check,
    CircleAlert, Server, Power, Eye, EyeOff, Copy, Globe, Lock, Activity,
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
} from 'recharts';
import { api, fmtDate, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DROPLETS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

function Gauge({ label, value, icon: Icon }) {
    const pct = value == null ? null : Math.max(0, Math.min(100, value));
    const color = pct == null
        ? 'bg-muted'
        : pct >= 85 ? 'bg-destructive'
        : pct >= 65 ? 'bg-warning'
        : 'bg-primary';
    return (
        <div className="rounded-sm border border-border bg-card p-5">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Icon size={14} /> {label}
                </div>
                <div className="font-display text-2xl font-black">
                    {pct == null ? '—' : `${pct}%`}
                </div>
            </div>
            <div className="mt-3 h-2 w-full bg-muted rounded-sm overflow-hidden">
                <div
                    className={`h-full transition-all ${color}`}
                    style={{ width: pct == null ? '0%' : `${pct}%` }}
                />
            </div>
        </div>
    );
}

export default function DropletDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [droplet, setDroplet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [poweringOn, setPoweringOn] = useState(false);
    const [resizeOpen, setResizeOpen] = useState(false);
    const [sizes, setSizes] = useState(null);
    const [sizesLoading, setSizesLoading] = useState(false);
    const [pick, setPick] = useState('');
    const [starting, setStarting] = useState(false);
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [pwLoading, setPwLoading] = useState(false);
    const [cpuHistory, setCpuHistory] = useState(null);
    const [cpuHistoryLoading, setCpuHistoryLoading] = useState(false);

    const loadCpuHistory = useCallback(async () => {
        setCpuHistoryLoading(true);
        try {
            const { data } = await api.get(`/droplets/${id}/cpu-history?hours=24`);
            setCpuHistory(data.points || []);
        } catch (e) { /* silent */ }
        finally { setCpuHistoryLoading(false); }
    }, [id]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/droplets/${id}`);
            setDroplet(data);
        } catch (e) {
            toast.error(formatApiError(e));
            nav('/droplets');
        } finally { setLoading(false); }
    }, [id, nav]);

    const doRefresh = useCallback(async (silent = false) => {
        setRefreshing(true);
        try {
            const { data } = await api.post(`/droplets/${id}/refresh`);
            setDroplet(data);
            if (!silent) toast.success('Insights refreshed');
        } catch (e) { if (!silent) toast.error(formatApiError(e)); }
        finally { setRefreshing(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh insights once when the detail page first has a droplet loaded.
    const [autoRefreshed, setAutoRefreshed] = useState(false);
    useEffect(() => {
        if (droplet && !autoRefreshed) {
            setAutoRefreshed(true);
            doRefresh(true); // silent
            loadCpuHistory();
        }
    }, [droplet, autoRefreshed, doRefresh, loadCpuHistory]);

    // Poll while resize is in progress
    useEffect(() => {
        if (!droplet) return undefined;
        const active = ['powering_off', 'resizing', 'powering_on'].includes(droplet.resize_status);
        if (!active) return undefined;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [droplet, load]);

    const doPowerOn = async () => {
        setPoweringOn(true);
        try {
            await api.post(`/droplets/${id}/power-on`);
            toast.success('Power-on requested. Refreshing…');
            // Give DO a moment then refresh
            setTimeout(() => load(), 3000);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPoweringOn(false); }
    };

    const openResize = async () => {
        setResizeOpen(true);
        setSizesLoading(true);
        setPick('');
        try {
            const { data } = await api.get(`/droplets/${id}/sizes`);
            setSizes(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSizesLoading(false); }
    };

    const startResize = async () => {
        if (!pick) return;
        setStarting(true);
        try {
            const { data } = await api.post(`/droplets/${id}/resize`, { size_slug: pick });
            setDroplet(data);
            toast.success('Resize started — droplet will be powered off, resized, and powered back on.');
            setResizeOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setStarting(false); }
    };

    const revealPassword = async () => {
        if (revealedPassword != null) {
            setRevealedPassword(null); // hide
            return;
        }
        setPwLoading(true);
        try {
            const { data } = await api.get(`/droplets/${id}/password`);
            setRevealedPassword(data.password);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPwLoading(false); }
    };

    const copyPassword = async () => {
        try {
            let pw = revealedPassword;
            if (pw == null) {
                const { data } = await api.get(`/droplets/${id}/password`);
                pw = data.password;
            }
            await navigator.clipboard.writeText(pw);
            toast.success('Password copied');
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !droplet) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-8 w-64 rounded-sm" />
                <div className="grid grid-cols-3 gap-4">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-sm" />)}
                </div>
            </div>
        );
    }

    const inProgress = ['powering_off', 'resizing', 'powering_on'].includes(droplet.resize_status);
    const insights = droplet.insights || {};

    return (
        <div data-testid={DROPLETS.detailRoot} className="space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => nav('/droplets')} className="rounded-sm">
                    <ArrowLeft size={14} className="mr-1.5" /> Back
                </Button>
            </div>

            {/* Header */}
            <div className="rounded-sm border border-border bg-card p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3">
                            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight truncate">
                                {droplet.name}
                            </h1>
                            <span className={`text-xs px-2 py-1 rounded-sm border ${
                                droplet.status === 'active' ? 'border-emerald-500/50 text-emerald-500' : 'text-muted-foreground border-border'
                            }`}>
                                {droplet.status}
                            </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                            <Server size={12} /> {droplet.region} · DO ID {droplet.do_droplet_id}
                        </div>
                        {(droplet.public_ip || droplet.private_ip) && (
                            <div className="text-sm mt-1 flex items-center gap-3 flex-wrap">
                                {droplet.public_ip && (
                                    <span className="flex items-center gap-1.5">
                                        <Globe size={12} className="text-primary" />
                                        <span className="text-muted-foreground text-xs">Public</span>
                                        <span className="font-mono">{droplet.public_ip}</span>
                                        <button
                                            onClick={async () => { await navigator.clipboard.writeText(droplet.public_ip); toast.success('IP copied'); }}
                                            className="text-muted-foreground hover:text-primary"
                                            aria-label="Copy public IP"
                                        >
                                            <Copy size={11} />
                                        </button>
                                    </span>
                                )}
                                {droplet.private_ip && (
                                    <span className="flex items-center gap-1.5">
                                        <Server size={12} className="text-muted-foreground" />
                                        <span className="text-muted-foreground text-xs">Private</span>
                                        <span className="font-mono">{droplet.private_ip}</span>
                                    </span>
                                )}
                            </div>
                        )}
                        {droplet.label && (
                            <div className="text-xs text-muted-foreground mt-1">{droplet.label}</div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {droplet.status !== 'active' && droplet.status !== 'powering_on' && !inProgress && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={doPowerOn}
                                disabled={poweringOn}
                                data-testid={DROPLETS.powerOnButton}
                                className="rounded-sm border-emerald-500/60 text-emerald-500 hover:bg-emerald-500/10"
                            >
                                {poweringOn ? <Loader2 className="animate-spin mr-1.5" size={13} /> : <Power size={13} className="mr-1.5" />}
                                Power on
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => doRefresh(false)}
                            disabled={refreshing || inProgress}
                            data-testid={DROPLETS.refreshButton}
                            className="rounded-sm"
                        >
                            {refreshing ? <Loader2 className="animate-spin mr-1.5" size={13} /> : <RefreshCw size={13} className="mr-1.5" />}
                            Refresh insights
                        </Button>
                        <Button
                            size="sm"
                            onClick={openResize}
                            disabled={inProgress}
                            data-testid={DROPLETS.resizeButton}
                            className="rounded-sm bg-primary hover:bg-primary/90"
                        >
                            <Sliders size={13} className="mr-1.5" /> Resize
                        </Button>
                    </div>
                </div>
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <div className="text-xs text-muted-foreground">Configuration</div>
                        <div className="font-semibold text-sm mt-1">{droplet.size_description}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Slug</div>
                        <div className="font-mono text-xs mt-1">{droplet.size_slug}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Monthly cost</div>
                        <div className="font-semibold text-sm mt-1">${droplet.monthly_cost}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Last refresh</div>
                        <div className="font-semibold text-xs mt-1">{fmtDate(droplet.insights_updated_at)}</div>
                    </div>
                </div>
                {inProgress && (
                    <div className="mt-4 rounded-sm bg-primary/10 border border-primary/40 px-3 py-2 text-sm flex items-center gap-2">
                        <Loader2 className="animate-spin text-primary" size={14} />
                        Resize in progress: <strong>{droplet.resize_status.replace('_', ' ')}</strong>
                        {droplet.resize_target_size && <> → <code className="font-mono">{droplet.resize_target_size}</code></>}
                    </div>
                )}
                {droplet.resize_status === 'failed' && droplet.resize_error && (
                    <div className="mt-4 rounded-sm bg-destructive/10 border border-destructive/40 px-3 py-2 text-sm flex items-center gap-2">
                        <CircleAlert size={14} className="text-destructive" />
                        Last resize failed: {droplet.resize_error}
                    </div>
                )}
                {/* Admin-only: stored password reveal + copy */}
                {isAdmin && droplet.has_password && (
                    <div className="mt-4 rounded-sm border border-border bg-muted/20 px-3 py-2 flex items-center gap-3 flex-wrap">
                        <Lock size={14} className="text-primary" />
                        <span className="text-sm text-muted-foreground">Stored root password</span>
                        <span
                            data-testid={DROPLETS.passwordValue}
                            className="font-mono text-sm px-2 py-0.5 rounded-sm bg-background border border-border select-all"
                        >
                            {revealedPassword != null
                                ? revealedPassword
                                : '••••••••••'}
                        </span>
                        <div className="flex items-center gap-1 ml-auto">
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={revealPassword}
                                disabled={pwLoading}
                                data-testid={DROPLETS.passwordReveal}
                                className="h-8 w-8 rounded-sm"
                                aria-label={revealedPassword != null ? 'Hide password' : 'Show password'}
                            >
                                {pwLoading ? <Loader2 className="animate-spin" size={13} />
                                    : revealedPassword != null ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={copyPassword}
                                data-testid={DROPLETS.passwordCopy}
                                className="h-8 w-8 rounded-sm"
                                aria-label="Copy password"
                            >
                                <Copy size={13} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Insights */}
            <div>
                <h2 className="font-display text-xl font-bold mb-3">Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Gauge label="CPU usage" value={insights.cpu_pct} icon={Cpu} />
                    <Gauge label="Memory usage" value={insights.memory_pct} icon={MemoryStick} />
                    <Gauge label="Disk usage" value={insights.disk_pct} icon={HardDrive} />
                </div>
                {(insights.cpu_pct == null && insights.memory_pct == null && insights.disk_pct == null) && (
                    <div className="text-xs text-muted-foreground mt-3">
                        Metrics unavailable. Ensure the DigitalOcean monitoring agent is installed on this droplet.
                    </div>
                )}
            </div>

            {/* 24h CPU chart */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-xl font-bold flex items-center gap-2">
                        <Activity size={18} /> CPU · last 24 hours
                    </h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadCpuHistory}
                        disabled={cpuHistoryLoading}
                        data-testid="droplet-cpu-history-refresh"
                        className="rounded-sm"
                    >
                        {cpuHistoryLoading ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />}
                    </Button>
                </div>
                <div className="rounded-sm border border-border bg-card p-5" data-testid="droplet-cpu-history-chart">
                    {cpuHistoryLoading && !cpuHistory ? (
                        <div className="h-64 grid place-items-center text-muted-foreground">
                            <Loader2 className="animate-spin" size={20} />
                        </div>
                    ) : !cpuHistory || cpuHistory.length === 0 ? (
                        <div className="h-64 grid place-items-center text-sm text-muted-foreground">
                            No CPU data available. The DO monitoring agent may not be installed on this droplet.
                        </div>
                    ) : (
                        <div style={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer>
                                <AreaChart data={cpuHistory} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                                    <defs>
                                        <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.5} />
                                            <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" />
                                    <XAxis
                                        dataKey="timestamp"
                                        type="number"
                                        domain={['dataMin', 'dataMax']}
                                        scale="time"
                                        stroke="hsl(var(--muted-foreground))"
                                        tick={{ fontSize: 11 }}
                                        tickFormatter={(t) => new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        stroke="hsl(var(--muted-foreground))"
                                        tick={{ fontSize: 11 }}
                                        tickFormatter={(v) => `${v}%`}
                                        domain={[0, 'auto']}
                                        width={44}
                                    />
                                    <ReTooltip
                                        contentStyle={{
                                            backgroundColor: 'hsl(var(--popover))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: 4, fontSize: 12,
                                        }}
                                        labelFormatter={(t) => new Date(t).toLocaleString()}
                                        formatter={(v) => [`${v}%`, 'CPU']}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="cpu_pct"
                                        stroke="hsl(var(--chart-1))"
                                        strokeWidth={1.6}
                                        fill="url(#cpuGradient)"
                                        isAnimationActive={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            {/* Resize dialog */}
            <Dialog open={resizeOpen} onOpenChange={setResizeOpen}>
                <DialogContent
                    data-testid={DROPLETS.resizeDialog}
                    className="bg-popover border-border rounded-sm max-w-3xl"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display">Resize {droplet.name}</DialogTitle>
                        <DialogDescription>
                            Pick a new size. The droplet will be powered off, resized, and powered back on automatically.
                            Options with less than {droplet.disk_gb} GB disk are hidden — DigitalOcean does not allow shrinking disks.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[55vh] overflow-y-auto">
                        {sizesLoading || !sizes ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Loader2 className="animate-spin inline" size={16} />
                            </div>
                        ) : sizes.sizes.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-6 text-center">
                                No alternative sizes available in this region.
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {sizes.sizes.map((s) => {
                                    const selected = pick === s.slug;
                                    return (
                                        <button
                                            key={s.slug}
                                            onClick={() => !s.is_current && setPick(s.slug)}
                                            disabled={s.is_current}
                                            data-testid={DROPLETS.resizeOption(s.slug)}
                                            className={[
                                                'w-full text-left px-4 py-3 rounded-sm border row-hover flex items-center justify-between gap-4',
                                                s.is_current
                                                    ? 'border-emerald-500/50 bg-emerald-500/5 opacity-70 cursor-not-allowed'
                                                    : selected
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-border hover:border-primary/50 hover:bg-muted/40',
                                            ].join(' ')}
                                        >
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium">{s.description}</div>
                                                <div className="font-mono text-xs text-muted-foreground mt-0.5">{s.slug}</div>
                                            </div>
                                            <div className="text-right shrink-0 flex items-center gap-3">
                                                <div>
                                                    <div className="text-lg font-black">${s.price_monthly}<span className="text-xs text-muted-foreground font-normal">/mo</span></div>
                                                    <div className="text-[10px] text-muted-foreground">${s.price_hourly}/hr</div>
                                                </div>
                                                {s.is_current && (
                                                    <span className="text-xs text-emerald-500 flex items-center gap-1"><Check size={12} /> current</span>
                                                )}
                                                {selected && !s.is_current && (
                                                    <span className="text-xs text-primary flex items-center gap-1"><Check size={12} /> selected</span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={startResize}
                            disabled={starting || !pick}
                            data-testid={DROPLETS.resizeConfirm}
                            className="rounded-sm bg-primary hover:bg-primary/90"
                        >
                            {starting ? <Loader2 className="animate-spin mr-2" size={14} /> : <Sliders size={14} className="mr-2" />}
                            Select and save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
